import {
  formatOmnibarStatusLabel,
  formatOmnibarTypeLabel,
  getOmnibarOperator,
  getOmnibarOperators,
  getOmnibarTypeValues,
  includesIgnoreCase,
  isOmnibarDrilldownOperator,
  OMNIBAR_STATUS_VALUES,
  parseOmnibarQuery,
  startsWithIgnoreCase,
} from './dashboardOmnibarFilters';
import {
  getMarketplaceSearchPath,
  MARKETPLACE_KEYWORD_MIN_LENGTH,
  normalizeMarketplaceKeyword,
} from '../marketplace/marketplaceKeywordSearch';
import { ADMIN_OMNIBAR_DESTINATIONS } from './adminOmnibarDestinations';

export const OMNIBAR_ENTITY_LIMIT = 8;
export const OMNIBAR_DESTINATION_LIMIT = 5;
export const OMNIBAR_GO_DESTINATION_LIMIT = 8;

const marketplaceBrowseLabel = (userType) => {
  if (userType === 'influencer') return 'Browse marketplace campaigns';
  if (userType === 'admin') return 'Search users';
  return 'Browse marketplace creators';
};

const marketplaceSearchLabel = (userType, keyword) => {
  if (userType === 'influencer') return `Search campaigns for “${keyword}”`;
  if (userType === 'admin') return `Search users for “${keyword}”`;
  return `Search creators for “${keyword}”`;
};

const matchEntityAgainstOperator = (entity, operator, value) => {
  if (operator === 'campaign') {
    return includesIgnoreCase(entity.campaignName || entity.label, value);
  }
  if (operator === 'creator') {
    return includesIgnoreCase(entity.creatorName || '', value);
  }
  if (operator === 'brand') {
    return includesIgnoreCase(entity.brandName || entity.label, value);
  }
  if (operator === 'status') {
    if (!value) return Boolean(entity.status);
    return startsWithIgnoreCase(entity.status || '', value)
      || includesIgnoreCase(entity.status || '', value);
  }
  if (operator === 'type') {
    if (!value) return Boolean(entity.type);
    return startsWithIgnoreCase(entity.type || '', value)
      || includesIgnoreCase(entity.type || '', value);
  }
  return includesIgnoreCase(entity.searchHaystack || entity.label, value);
};

const matchFreeText = (row, needle) => (
  includesIgnoreCase(row.searchHaystack || row.label, needle)
  || includesIgnoreCase(row.description || '', needle)
);

const buildFilterCategoryRows = (userType) => (
  getOmnibarOperators(userType).map((op) => ({
    id: `filter-category-${op.key}`,
    kind: 'filter-category',
    operatorKey: op.key,
    label: op.friendlyLabel,
    description: op.description,
    insertText: op.insertText,
    drilldown: isOmnibarDrilldownOperator(op.key),
  }))
);

export const buildStatusCompletions = (value = '') => (
  OMNIBAR_STATUS_VALUES
    .filter((status) => (
      startsWithIgnoreCase(status, value)
      || startsWithIgnoreCase(formatOmnibarStatusLabel(status), value)
    ))
    .map((status) => ({
      id: `completion-status-${status}`,
      kind: 'filter-completion',
      label: formatOmnibarStatusLabel(status),
      description: 'Filter by status',
      insertText: `status:${status} `,
      operatorKey: 'status',
      value: status,
    }))
);

export const buildTypeCompletions = (userType, value = '') => (
  getOmnibarTypeValues(userType)
    .filter((type) => (
      startsWithIgnoreCase(type, value)
      || startsWithIgnoreCase(formatOmnibarTypeLabel(type), value)
    ))
    .map((type) => ({
      id: `completion-type-${type}`,
      kind: 'filter-completion',
      label: formatOmnibarTypeLabel(type),
      description: 'Filter by type',
      insertText: `type:${type} `,
      operatorKey: 'type',
      value: type,
    }))
);

const buildGoDestinationRows = (destinations, value = '') => (
  destinations
    .filter((row) => matchFreeText(row, value))
    .slice(0, OMNIBAR_GO_DESTINATION_LIMIT)
    .map((row) => ({
      ...row,
      kind: 'destination',
    }))
);

/**
 * Empty-focus panel: plain-language filter categories + browse marketplace footer.
 */
export const getEmptyOmnibarSuggestions = (userType) => {
  if (userType === 'admin') {
    return [
      ...ADMIN_OMNIBAR_DESTINATIONS,
      {
        id: 'admin-search-users',
        kind: 'marketplace-browse',
        label: marketplaceBrowseLabel(userType),
        description: 'Users',
        href: '/admin/users',
      },
    ];
  }

  const filters = buildFilterCategoryRows(userType);
  return [
    ...filters,
    {
      id: 'marketplace-browse',
      kind: 'marketplace-browse',
      label: marketplaceBrowseLabel(userType),
      description: 'Marketplace',
      href: getMarketplaceSearchPath(userType),
    },
  ];
};

/**
 * Category drill-down picks (Status / Type / Go to page) before a prefix is inserted.
 */
export const getCategoryDrilldownSuggestions = (
  categoryMode,
  userType,
  index = {},
  filterText = '',
) => {
  const needle = String(filterText || '').trim();

  if (categoryMode === 'status') {
    return buildStatusCompletions(needle);
  }
  if (categoryMode === 'type') {
    return buildTypeCompletions(userType, needle);
  }
  if (categoryMode === 'go') {
    return buildGoDestinationRows(index.destinations || [], needle);
  }
  return [];
};

/**
 * Typed panel: filter completions, destinations, entities, marketplace fallback.
 */
export const getTypedOmnibarSuggestions = (query, userType, index = {}) => {
  const destinations = index.destinations || [];
  const entities = index.entities || [];
  const parsed = parseOmnibarQuery(query, userType);
  const items = [];

  if (parsed.operator === 'status') {
    items.push(...buildStatusCompletions(parsed.value));
  } else if (parsed.operator === 'type') {
    items.push(...buildTypeCompletions(userType, parsed.value));
  } else if (parsed.operator === 'go') {
    // Destinations are added below via matchedDestinations.
  } else if (parsed.operator && !parsed.value) {
    const op = getOmnibarOperator(userType, parsed.operator);
    if (op) {
      items.push({
        id: `filter-category-${op.key}-active`,
        kind: 'filter-category',
        operatorKey: op.key,
        label: op.friendlyLabel,
        description: op.description,
        insertText: op.insertText,
        drilldown: false,
      });
    }
  }

  let matchedDestinations = destinations;
  let matchedEntities = entities;

  if (parsed.operator === 'go') {
    matchedEntities = [];
    matchedDestinations = destinations.filter((row) => matchFreeText(row, parsed.value));
  } else if (parsed.operator) {
    matchedDestinations = [];
    matchedEntities = entities.filter((entity) => (
      matchEntityAgainstOperator(entity, parsed.operator, parsed.value)
    ));
  } else if (parsed.freeText) {
    matchedDestinations = destinations.filter((row) => matchFreeText(row, parsed.freeText));
    matchedEntities = entities.filter((row) => matchFreeText(row, parsed.freeText));
  }

  const destinationLimit = parsed.operator === 'go'
    ? OMNIBAR_GO_DESTINATION_LIMIT
    : OMNIBAR_DESTINATION_LIMIT;

  items.push(
    ...matchedDestinations.slice(0, destinationLimit).map((row) => ({
      ...row,
      kind: 'destination',
    })),
  );
  items.push(
    ...matchedEntities.slice(0, OMNIBAR_ENTITY_LIMIT).map((row) => ({
      ...row,
      kind: 'entity',
    })),
  );

  if (!parsed.operator) {
    const keyword = normalizeMarketplaceKeyword(parsed.freeText || query);
    if (keyword.length >= MARKETPLACE_KEYWORD_MIN_LENGTH) {
      const isAdmin = userType === 'admin';
      items.push({
        id: `marketplace-search-${keyword}`,
        kind: 'marketplace',
        label: marketplaceSearchLabel(userType, keyword),
        description: isAdmin ? 'Users' : 'Marketplace',
        keyword,
        href: isAdmin ? '/admin/users' : getMarketplaceSearchPath(userType),
      });
    }
  }

  return items;
};
